/**
 * @file     chunkLoadError.ts
 * @purpose  Detect ChunkLoadErrors (stale JS chunks after a new deploy) and
 *           recover by reloading. These are deploy-skew artifacts, not real
 *           crashes — they must not trigger crash-report emails.
 */

const CHUNK_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Failed to load chunk',
  'Loading chunk',
  'Loading CSS chunk',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

/** True when the error is a stale-chunk failure caused by a new deploy. */
export function isChunkLoadError(
  error: { name?: string; message?: string } | null | undefined,
): boolean {
  if (!error) return false;
  const hay = `${error.name ?? ''} ${error.message ?? ''}`;
  return CHUNK_ERROR_PATTERNS.some((p) => hay.includes(p));
}

/**
 * Reload the page once to pick up fresh chunks. Time-boxed so a chunk that
 * genuinely keeps failing (e.g. offline) can't trigger a reload loop —
 * after a reload it falls through to the normal error UI.
 * Returns true if a reload was triggered.
 */
export function recoverFromChunkLoadError(): boolean {
  if (typeof window === 'undefined') return false;
  const KEY = 'relta:chunk-reload-at';
  try {
    const last = Number(window.sessionStorage.getItem(KEY) ?? '0');
    if (Date.now() - last < 30_000) return false;
    window.sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — skip the loop guard, reload anyway once.
  }
  window.location.reload();
  return true;
}
